/**
 * Optimized Product Card Component
 * Uses React.memo to prevent unnecessary re-renders
 * Implements lazy loading for images
 * Includes promotion support with discount badges
 *
 * Layout variants per client theme:
 *   - horizontal (default / royal-dark / cocoa-lounge / neon-bistro)
 *   - vertical   (classic-coffeeshop / soft-pebble)
 *   - compact    (sunbloom — used inside a 2-col grid via .products-list CSS)
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Product } from '../../types/menu';
import { getImageUrl } from '../../config/api';

interface Promotion {
    id: number;
    name: string;
    type: 'percentage' | 'fixed_amount';
    value: number;
}

interface ProductCardProps {
    product: Product;
    currency: string;
    isMenuViewOnly: boolean;
    onSelect: (product: Product) => void;
    onAddToCart: (product: Product) => void;
    promotion?: Promotion;
    theme?: string;
}

type Variant = 'horizontal' | 'vertical' | 'compact';

const getVariant = (theme: string | undefined): Variant => {
    switch (theme) {
        case 'sunbloom':
            return 'compact';
        case 'classic-coffeeshop':
        case 'soft-pebble':
            return 'vertical';
        default:
            return 'horizontal';
    }
};

const ProductCard: React.FC<ProductCardProps> = React.memo(({
    product,
    currency,
    isMenuViewOnly,
    onSelect,
    onAddToCart,
    promotion,
    theme
}) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const handleImageLoad = useCallback(() => setImageLoaded(true), []);
    const handleImageError = useCallback(() => setImageError(true), []);

    const handleAddToCartClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        onAddToCart(product);
    }, [product, onAddToCart]);

    const handleCardClick = useCallback(() => {
        onSelect(product);
    }, [product, onSelect]);

    const priceInfo = useMemo(() => {
        const originalPrice = Number(product.price);
        if (!promotion) {
            return { original: originalPrice, final: originalPrice, savings: 0 };
        }
        let discountedPrice = originalPrice;
        if (promotion.type === 'percentage') {
            discountedPrice = originalPrice * (1 - promotion.value / 100);
        } else {
            discountedPrice = originalPrice - promotion.value;
        }
        discountedPrice = Math.max(0, discountedPrice);
        return { original: originalPrice, final: discountedPrice, savings: originalPrice - discountedPrice };
    }, [product.price, promotion]);

    const variant = getVariant(theme);
    const isHorizontal = variant === 'horizontal';
    const isCompact = variant === 'compact';

    /* ─── Image block (shared) ────────────────────────────────────────── */
    const imageBlock = (
        <div
            className="product-card__image"
            style={{
                width: isHorizontal ? '120px' : '100%',
                height: isCompact ? '140px' : isHorizontal ? '120px' : '180px',
                flexShrink: 0,
                borderRadius: '0.75rem',
                overflow: 'hidden',
                background: 'rgba(255, 255, 255, 0.05)',
                position: 'relative'
            }}
        >
            {promotion && (
                <div style={{
                    position: 'absolute',
                    top: '0.5rem',
                    left: '0.5rem',
                    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                    color: '#8B0000',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    zIndex: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}>
                    {promotion.type === 'percentage' ? `${promotion.value}% OFF` : `$${promotion.value} OFF`}
                </div>
            )}

            {!imageLoaded && !imageError && product.image_url && (
                <div
                    className="skeleton-pulse"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 1.5s infinite'
                    }}
                />
            )}

            {product.image_url && !imageError ? (
                <img
                    src={getImageUrl(product.image_url) || ''}
                    alt={product.name}
                    loading="lazy"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: imageLoaded ? 1 : 0,
                        transition: 'opacity 0.3s ease-in-out'
                    }}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                />
            ) : (
                <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isCompact ? '2.25rem' : '3rem'
                }}>
                    🍽️
                </div>
            )}
        </div>
    );

    /* ─── Title ───────────────────────────────────────────────────────── */
    const title = (
        <h3 className="product-card__title" style={{
            color: 'white',
            fontSize: isCompact ? '1rem' : '1.125rem',
            fontWeight: 'bold',
            marginBottom: '0.35rem'
        }}>
            {product.name}
        </h3>
    );

    /* ─── Description (skipped on compact) ────────────────────────────── */
    const description = !isCompact && product.description ? (
        <p className="product-card__desc" style={{
            color: 'rgba(255, 255, 255, 0.6)',
            fontSize: '0.875rem',
            lineHeight: '1.4',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
        }}>
            {product.description}
        </p>
    ) : null;

    /* ─── Price block ─────────────────────────────────────────────────── */
    const priceBlock = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {promotion ? (
                <>
                    <span style={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        fontSize: '0.875rem',
                        textDecoration: 'line-through'
                    }}>
                        {Math.round(priceInfo.original)} {currency}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <span style={{
                            color: '#4ADE80',
                            fontSize: isCompact ? '1rem' : '1.25rem',
                            fontWeight: 'bold'
                        }}>
                            {Math.round(priceInfo.final)} {currency}
                        </span>
                        <span style={{
                            background: '#4ADE80',
                            color: '#1F2937',
                            fontSize: '0.625rem',
                            padding: '0.125rem 0.375rem',
                            borderRadius: '0.25rem',
                            fontWeight: 'bold'
                        }}>
                            SAVE {Math.round(priceInfo.savings)} {currency}
                        </span>
                    </div>
                </>
            ) : (
                <span className="product-card__price" style={{
                    color: '#FFD700',
                    fontSize: isCompact ? '1rem' : '1.25rem',
                    fontWeight: 'bold'
                }}>
                    {Math.round(Number(product.price))} {currency}
                </span>
            )}
        </div>
    );

    /* ─── Add button ──────────────────────────────────────────────────── */
    const addButton = !isMenuViewOnly ? (
        <button
            onClick={handleAddToCartClick}
            aria-label={`Add ${product.name} to cart`}
            className="product-card__add-btn"
            style={{
                background: 'rgba(139, 0, 0, 0.8)',
                border: 'none',
                borderRadius: '8px',
                padding: isCompact ? '0.4rem 0.75rem' : '0.5rem 1rem',
                color: 'white',
                fontSize: isCompact ? '0.8rem' : '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 1)'}
            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(139, 0, 0, 0.8)'}
        >
            + Add
        </button>
    ) : null;

    /* ─── Layout: horizontal ──────────────────────────────────────────── */
    if (isHorizontal) {
        return (
            <div
                className={`glass-dark product-card product-card--horizontal product-card--theme-${theme || 'royal-dark'}`}
                style={{
                    borderRadius: '1rem',
                    padding: '1rem',
                    display: 'flex',
                    gap: '1rem',
                    transition: 'all 0.3s',
                    cursor: 'pointer'
                }}
                onClick={handleCardClick}
            >
                {imageBlock}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                    <div>
                        {title}
                        {description}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', gap: '0.5rem' }}>
                        {priceBlock}
                        {addButton}
                    </div>
                </div>
            </div>
        );
    }

    /* ─── Layout: vertical / compact ──────────────────────────────────── */
    return (
        <div
            className={`glass-dark product-card product-card--${variant} product-card--theme-${theme || 'royal-dark'}`}
            style={{
                borderRadius: '1rem',
                padding: isCompact ? '0.75rem' : '1rem',
                display: 'flex',
                flexDirection: 'column',
                gap: isCompact ? '0.5rem' : '0.75rem',
                transition: 'all 0.3s',
                cursor: 'pointer'
            }}
            onClick={handleCardClick}
        >
            {imageBlock}
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                {title}
                {description}
            </div>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.5rem',
                flexWrap: isCompact ? 'wrap' : 'nowrap'
            }}>
                {priceBlock}
                {addButton}
            </div>
        </div>
    );
}, (prevProps, nextProps) => {
    return (
        prevProps.product.id === nextProps.product.id &&
        prevProps.product.is_available === nextProps.product.is_available &&
        prevProps.product.price === nextProps.product.price &&
        prevProps.currency === nextProps.currency &&
        prevProps.isMenuViewOnly === nextProps.isMenuViewOnly &&
        prevProps.promotion?.id === nextProps.promotion?.id &&
        prevProps.promotion?.value === nextProps.promotion?.value &&
        prevProps.theme === nextProps.theme
    );
});

ProductCard.displayName = 'ProductCard';

export default ProductCard;
